// Thin fetch wrapper for the Slot backend.
// Uses /api as the base path — Vite proxies that to the PHP dev server.

import type { Appointment, FinderConfig, HolidayCountry } from './types';

/**
 * Normalise legacy survey config blobs. Older versions of the app stored a
 * single `holidayCountry` field; the new schema uses `holidayCountries: []`.
 * Convert in-place when reading back from the server.
 */
function normalizeConfig(raw: unknown): FinderConfig {
  const c = (raw as Record<string, unknown> | null) ?? {};
  const obj = { ...c } as Record<string, unknown>;
  if (!Array.isArray(obj.holidayCountries)) {
    if (obj.holidayCountry === null || obj.holidayCountry === undefined) {
      obj.holidayCountries = [];
    } else if (typeof obj.holidayCountry === 'string') {
      obj.holidayCountries = [obj.holidayCountry as HolidayCountry];
    } else {
      obj.holidayCountries = [];
    }
  }
  delete obj.holidayCountry;
  return obj as FinderConfig;
}

export type CreateSurveyPayload = {
  title?: string;
  config: FinderConfig;
  slots: Appointment[];
  /**
   * Unix seconds when the survey should be auto-deleted.
   *  - omit (undefined) → backend default (14 days after last slot)
   *  - -1 → never expire
   *  - any int → that exact moment
   */
  expires_at?: number;
  /** Maximum unique voters that may submit. null = unbegrenzt, default 50.
   *  Server clamped at 1..500. Siehe security.html §4. */
  vote_cap?: number | null;
  voter_results_visibility?: VoterVisibility;
  /** Optionaler Ort/Link für die ganze Survey, max 200 Zeichen. */
  location?: string | null;
  /** Unix-Sekunden: bis dahin können Stimmen abgegeben werden. Nach diesem
   *  Zeitpunkt lehnt der Server neue/geänderte Stimmen ab. null/undefined
   *  = keine Deadline. */
  voting_deadline?: number | null;
};

export type CreateSurveyResponse = {
  id: string;
  admin_token: string;
  voter_token: string;
  expires_at: number | null;
  vote_cap: number | null;
};

export type SurveyVote = {
  pseudonym: string;
  responses: Record<string, 'yes' | 'maybe' | 'no'>;
  created_at: number;
  updated_at: number;
};

export type VoterVisibility = 'none' | 'aggregate' | 'full';

export type VoteAggregateRow = {
  slot_id: string;
  yes: number;
  maybe: number;
  no: number;
};

export type Message = {
  id: number;
  pseudonym: string;
  text: string;
  visibility: 'public' | 'admin';
  created_at: number;
  is_own?: boolean;
};

export type AdminSurveyResponse = {
  id: string;
  admin_token: string;
  voter_token: string;
  title: string | null;
  config: FinderConfig;
  slots: Appointment[];
  created_at: number;
  expires_at: number | null;
  closed_at: number | null;
  voter_results_visibility: VoterVisibility;
  /** Wenn finalisiert: Slot-IDs aus dem slots-Blob, sonst null/leer. */
  final_slot_ids: string[] | null;
  /** Optionale Klartext-Nachricht des Admins beim Finalisieren. */
  final_message: string | null;
  /** Optionaler Ort/Link für die ganze Survey. */
  location: string | null;
  /** Aktueller Vote-Cap (1..500, null = unbegrenzt). */
  vote_cap: number | null;
  /** Unix-Sekunden für Abstimmungs-Deadline. null = keine. */
  voting_deadline: number | null;
  votes: SurveyVote[];
  messages: Message[];
};

export type VoterSurveyResponse = {
  id: string;
  voter_token: string;
  title: string | null;
  config: FinderConfig;
  slots: Appointment[];
  created_at: number;
  expires_at: number | null;
  closed_at: number | null;
  voter_results_visibility: VoterVisibility;
  final_slot_ids: string[] | null;
  final_message: string | null;
  /** Optionaler Ort/Link für die ganze Survey. */
  location: string | null;
  /** Unix-Sekunden für Abstimmungs-Deadline. null = keine. */
  voting_deadline: number | null;
  /** Present only when visibility is "aggregate" or "full". */
  vote_aggregate?: VoteAggregateRow[];
  /** Present only when visibility is "full". */
  votes?: SurveyVote[];
  messages: Message[];
};

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // body wasn't JSON, keep default message
    }
    throw new ApiError(response.status, message);
  }
  return (await response.json()) as T;
}

export function createSurvey(payload: CreateSurveyPayload): Promise<CreateSurveyResponse> {
  return request<CreateSurveyResponse>('/survey', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getAdminSurvey(adminToken: string): Promise<AdminSurveyResponse> {
  const res = await request<AdminSurveyResponse>(`/survey/admin/${encodeURIComponent(adminToken)}`);
  return { ...res, config: normalizeConfig(res.config) };
}

export async function getVoterSurvey(
  voterToken: string,
  voterId?: string,
): Promise<VoterSurveyResponse> {
  const qs = voterId ? `?voter_id=${encodeURIComponent(voterId)}` : '';
  const res = await request<VoterSurveyResponse>(
    `/survey/vote/${encodeURIComponent(voterToken)}${qs}`,
  );
  return { ...res, config: normalizeConfig(res.config) };
}

export type SendMessagePayload = {
  voter_id: string;
  pseudonym: string;
  text: string;
  visibility: 'public' | 'admin';
};

export function sendMessage(
  voterToken: string,
  payload: SendMessagePayload,
): Promise<Message> {
  return request<Message>(
    `/survey/vote/${encodeURIComponent(voterToken)}/messages`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export type SubmitVotePayload = {
  voter_id: string;
  pseudonym: string;
  responses: Record<string, 'yes' | 'maybe' | 'no'>;
  pow_challenge?: string;
  pow_nonce?: string;
};

export type SubmitVoteResponse = {
  voter_id: string;
  pseudonym: string;
  responses: Record<string, 'yes' | 'maybe' | 'no'>;
  updated_at: number;
  /** Nur beim ERSTEN Submit gesetzt — Edit-Token im Klartext, damit der Voter
   *  ihn als Bookmark/Copy/HTML speichern kann. Spätere Submits liefern
   *  diesen Wert nicht mehr (Server speichert nur den Hash). */
  edit_token?: string;
};

export type ResolveEditTokenResponse = {
  voter_id: string;
  pseudonym: string;
  responses: Record<string, 'yes' | 'maybe' | 'no'>;
  updated_at: number;
};

export function resolveEditToken(
  voterToken: string,
  editToken: string,
): Promise<ResolveEditTokenResponse> {
  const u = new URLSearchParams({ t: editToken });
  return request<ResolveEditTokenResponse>(
    `/survey/vote/${encodeURIComponent(voterToken)}/resolve?${u.toString()}`,
  );
}

export type FeedTokenResponse = { feed_token: string; feed_url: string };

export function ensureFeedToken(
  voterToken: string,
  voterId: string,
): Promise<FeedTokenResponse> {
  return request<FeedTokenResponse>(
    `/survey/vote/${encodeURIComponent(voterToken)}/feed-token`,
    {
      method: 'POST',
      body: JSON.stringify({ voter_id: voterId }),
    },
  );
}

export function submitVote(
  voterToken: string,
  payload: SubmitVotePayload,
): Promise<SubmitVoteResponse> {
  return request<SubmitVoteResponse>(
    `/survey/vote/${encodeURIComponent(voterToken)}/responses`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export type MyVoteResponse = {
  vote: {
    voter_id: string;
    pseudonym: string;
    responses: Record<string, 'yes' | 'maybe' | 'no'>;
    created_at: number;
    updated_at: number;
  } | null;
};

export function getMyVote(voterToken: string, voterId: string): Promise<MyVoteResponse> {
  const u = new URLSearchParams({ voter_id: voterId });
  return request<MyVoteResponse>(
    `/survey/vote/${encodeURIComponent(voterToken)}/me?${u.toString()}`,
  );
}

export type SurveyAdminUpdate = {
  voter_results_visibility?: VoterVisibility;
  /** Slot-IDs für Finalisierung. Leeres Array oder null hebt Finalisierung auf. */
  final_slot_ids?: string[] | null;
  final_message?: string | null;
  /** Inhaltsänderungen — Tokens und ID bleiben dabei stabil, schon
   *  verschickte Voter-Links funktionieren weiter. */
  title?: string | null;
  location?: string | null;
  slots?: Appointment[];
  config?: FinderConfig;
  vote_cap?: number | null;
  voting_deadline?: number | null;
};

export function updateSurvey(adminToken: string, patch: SurveyAdminUpdate): Promise<SurveyAdminUpdate> {
  return request<SurveyAdminUpdate>(`/survey/admin/${encodeURIComponent(adminToken)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}
